import { Formik, Field, Form, ErrorMessage } from 'formik';
import { Button, Box, Grid, GridItem, Text, useToast } from '@chakra-ui/react';
import * as Yup from 'yup';
import React from 'react';

import useAuth from 'hooks/useAuth';
import { LabelForm, FormColumns } from 'components/wrapper';
import xhr from 'xhr';
import globals from 'styles/globals.module.css';
import useSWR from 'swr';
import getIdentityTypes from 'api/profile/getIdentityTypes';
import { diffFields, logStudentChanges } from './audit';
import MunicipalityInput from './MunicipalityInput';
import { sanitizeDocumentNumber } from 'utils/documentNumber';

// El lugar de expedición ya registrado se acepta tal cual; si se cambia, no puede llevar números.
const buildIdentitySchema = (currentPlace) =>
  Yup.object().shape({
    identity_type: Yup.string().required('Requerido'),
    identity: Yup.string().required('Requerido'),
    expedition_place: Yup.string()
      .required('Requerido')
      .test('sin-numeros', 'Escribe solo el lugar, sin fechas ni números.', (value) => value === currentPlace || !/\d/.test(value || '')),
  });

const Identity = (props) => {
  const [loading, setLoading] = React.useState(false);
  const { student, mutate } = props;

  const toasts = useToast();
  const { user } = useAuth();
  const { data, error } = useSWR('identity', getIdentityTypes, { revalidateOnFocus: false });
  if (!data) return 'Cargando...';

  const typeNameOf = (id) => data.find((el) => String(el.id) === String(id))?.name;

  const handleIdentity = async (values) => {
    try {
      setLoading(true);

      const res = await xhr.put(`/students/${student.id}/identity`, values);

      const typeName = (id) => data.find((el) => String(el.id) === String(id))?.name || id;
      const before = {
        identity_type: typeName(student.identity_type && student.identity_type.id),
        identity: student.identity,
        expedition_place: student.expedition_place,
      };
      await logStudentChanges({
        studentId: student.id,
        userId: user.id,
        section: 'documento personal',
        changes: diffFields(before, { ...values, identity_type: typeName(values.identity_type) }, {
          identity_type: 'Tipo de documento',
          identity: 'Número de documento',
          expedition_place: 'Lugar de expedición',
        }),
      });

      mutate({ ...res.data, user: student.user }, { revalidate: false });

      toasts({
        description: 'Tus datos se han guardado correctamente!',
        status: 'success',
        accent: 'bottom',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  return (
    <Formik
      initialValues={{
        identity_type: (student.identity_type && student.identity_type.id) || '',
        identity: student.identity || '',
        expedition_place: student.expedition_place || '',
      }}
      validationSchema={buildIdentitySchema(student.expedition_place || '')}
      onSubmit={handleIdentity}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form>
            <Box marginY="11px">
              <Text fontSize="23px" fontWeight="500" marginBottom="20px">
                Datos de tu documento personal
              </Text>
              <Box boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 #574feb" borderRadius="5px" bg="white">
                <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
                  <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
                    <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
                      <LabelForm>Tipo de documento</LabelForm>
                    </GridItem>

                    <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
                      <Field
                        as="select"
                        name="identity_type"
                        className={`${globals.input} ${globals.nombres}`}
                        placeholder="Seleccionar"
                        onChange={(e) => {
                          const typeId = e.target.value;
                          setFieldValue('identity_type', typeId);
                          // Al cambiar el tipo, un número editado se ajusta a la regla del nuevo tipo.
                          if (values.identity && values.identity !== (student.identity || '')) {
                            setFieldValue('identity', sanitizeDocumentNumber(values.identity, typeNameOf(typeId)));
                          }
                        }}
                      >
                        {data.map((el) => (
                          <option key={el.id} value={el.id}>
                            {el.name}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage name={'identity_type'}>
                        {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                      </ErrorMessage>
                    </GridItem>
                  </Grid>
                </FormColumns>

                <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
                  {' '}
                  <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
                    <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
                      <LabelForm>Número de documento</LabelForm>
                    </GridItem>

                    <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
                      <Field
                        name="identity"
                        className={`${globals.input} ${globals.nombres}`}
                        // Solo dígitos, salvo pasaporte (letras y números).
                        onChange={(e) =>
                          setFieldValue('identity', sanitizeDocumentNumber(e.target.value, typeNameOf(values.identity_type)))
                        }
                      />

                      <ErrorMessage name={'identity'}>
                        {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                      </ErrorMessage>
                    </GridItem>
                  </Grid>
                </FormColumns>

                <FormColumns borderBottom="1px solid #e6e6eb" paddingBottom="10px" marginBottom="10px">
                  <Grid templateColumns="repeat(4, 1fr)" alignItems="center">
                    <GridItem colSpan={1} paddingTop="10px" paddingLeft="20px">
                      <LabelForm>Lugar de expedición</LabelForm>
                    </GridItem>

                    <GridItem colSpan={3} paddingTop="10px" paddingRight="20px">
                      <Field name="expedition_place">
                        {({ field, form }) => (
                          <MunicipalityInput
                            value={field.value}
                            initialValue={student.expedition_place || ''}
                            onChange={(place) => form.setFieldValue('expedition_place', place)}
                          />
                        )}
                      </Field>

                      <ErrorMessage name={'expedition_place'}>
                        {(msg) => <div style={{ color: 'red', fontSize: 13 }}>{msg}</div>}
                      </ErrorMessage>
                    </GridItem>
                  </Grid>
                </FormColumns>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <Button type="submit" colorScheme="purple" marginY="15px" width="140px" isLoading={loading}>
                Actualizar
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Identity;
